import { Component, Input, OnInit, TemplateRef } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { AttachmentBase } from '@sb-tools/typescript/models/attachment-base.model';
import { Field } from '@sb-tools/typescript/models/field.model';
import { FieldType } from '@sb-tools/typescript/enums/field-type.enum';
import { FormType } from '@sb-tools/typescript/enums/form-type.enum';
import { FormService } from '../common/services/form.service';
import { ClientField } from '../common/models/client-field.model';
import { MenyProductField } from '../common/enums/meny-product-field.enum';
import { UserIdentificationComponent } from '@form-field-module/form-field/main-fields/user-identification/user-identification.component';
import { TextInputComponent } from '@form-field-module/form-field/other-fields/text-input/text-input.component';
import { FormHeaderComponent } from '@form-field-module/form-field/main-fields/form-header/form-header.component';
import { SentimentComponent } from '@form-field-module/form-field/main-fields/sentiment/sentiment.component';
import { MessageComponent } from '@form-field-module/form-field/main-fields/message/message.component';
import { TitleComponent } from '@form-field-module/form-field/main-fields/title/title.component';
import { AttachmentComponent } from '@form-field-module/form-field/main-fields/attachment/attachment.component';
import { CategoriesComponent } from '@form-field-module/form-field/main-fields/categories/categories.component';
import { CcUsersComponent } from '@form-field-module/form-field/internal/cc-users/cc-users.component';
import { OutboundEmailAddressComponent } from '@form-field-module/form-field/internal/outbound-email-address/outbound-email-address.component';
import { SendToClientCheckboxComponent } from '@form-field-module/form-field/internal/send-to-client-checkbox/send-to-client-checkbox.component';
import { FormattedTextComponent } from '@form-field-module/form-field/other-fields/formatted-text/formatted-text.component';
import { TextareaComponent } from '@form-field-module/form-field/other-fields/textarea/textarea.component';
import { SelectComponent } from '@form-field-module/form-field/other-fields/select/select.component';
import { NumberInputComponent } from '@form-field-module/form-field/other-fields/number-input/number-input.component';
import { RadioComponent } from '@form-field-module/form-field/other-fields/radio/radio.component';
import { CheckboxComponent } from '@form-field-module/form-field/other-fields/checkbox/checkbox.component';
import { MobileNumberComponent } from '@form-field-module/form-field/other-fields/mobile-number/mobile-number.component';
import { AddressComponent } from '@form-field-module/form-field/other-fields/address/address.component';
import { ChecklistComponent } from '@form-field-module/form-field/other-fields/checklist/checklist.component';
import { EmailInputComponent } from '@form-field-module/form-field/other-fields/email-input/email-input.component';
import { DateComponent } from '@form-field-module/form-field/other-fields/date/date.component';
import { AccountNumberComponent } from '@form-field-module/form-field/other-fields/account-number/account-number.component';
import { StoreInfoComponent } from '@form-field-module/form-field/other-fields/store-info/store-info.component';
import { MenyProductComponent } from '@form-field-module/form-field/other-fields/meny-product/meny-product.component';
import { MenyProductMetaFieldComponent } from '@form-field-module/form-field/other-fields/meny-product-meta-field/meny-product-meta-field.component';
import { TicketStatusComponent } from '@form-field-module/form-field/outlook/ticket-status/ticket-status.component';
import { AssigneeComponent } from '@form-field-module/form-field/outlook/assignee/assignee.component';
import { CommunitySelectorComponent } from '@form-field-module/form-field/outlook/community-selector/community-selector.component';
import { StarRatingComponent } from '@form-field-module/form-field/other-fields/star-rating/star-rating.component';
import { CheckCategoryValueDirective } from '@form-field-module/common/directives/check-category-value.directive';
import { LocalizationLanguage } from '@sb-tools/typescript/enums/localization-language.enum';
import { ReadOnlyFieldComponent } from '../common/components/read-only-field/read-only-field.component';

@Component({
    selector: 'sb-form-field',
    templateUrl: './form-field.component.html',
    styleUrls: ['./form-field.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    imports: [
        UserIdentificationComponent,
        TextInputComponent,
        FormHeaderComponent,
        SentimentComponent,
        MessageComponent,
        TitleComponent,
        AttachmentComponent,
        CategoriesComponent,
        CcUsersComponent,
        OutboundEmailAddressComponent,
        SendToClientCheckboxComponent,
        FormattedTextComponent,
        TextareaComponent,
        SelectComponent,
        NumberInputComponent,
        RadioComponent,
        CheckboxComponent,
        MobileNumberComponent,
        AddressComponent,
        ChecklistComponent,
        EmailInputComponent,
        DateComponent,
        AccountNumberComponent,
        StoreInfoComponent,
        MenyProductComponent,
        MenyProductMetaFieldComponent,
        TicketStatusComponent,
        AssigneeComponent,
        CommunitySelectorComponent,
        StarRatingComponent,
        CheckCategoryValueDirective,
        ReadOnlyFieldComponent,
    ],
})
export class FormFieldComponent implements OnInit {
    @Input() formField: Field | ClientField;
    @Input() formType?: FormType = FormType.webExternal;
    @Input() isElementNameShown?: boolean;
    @Input() isShowHiddenFields?: boolean;
    @Input() templateRef?: TemplateRef<any>;
    @Input() attachments?: AttachmentBase[];
    @Input() formLanguage?: LocalizationLanguage;

    fieldTypesEnum = FieldType;
    formTypes = FormType;
    menyProductFieldEnum = MenyProductField;

    constructor(public formService: FormService) {}

    get isReadOnly(): boolean {
        return this.formField.isReadOnly === true && this.formType !== FormType.inboxSearch;
    }

    ngOnInit() {
        this.formField.isRequired =
            this.formType === FormType.inboxEditFields
                ? (this.formField as Field).isRequiredForTeam
                : this.formField.isRequired;

        if (
            [FormType.webExternal, FormType.webInternal, FormType.widgetAuthentication, FormType.outlook].indexOf(
                this.formType,
            ) > -1
        ) {
            this.formField.value = this.isReadOnly
                ? (this.formField.value ?? this.formField.defaultValue)
                : this.formField.value || this.formField.defaultValue;
        }

        if (this.formType !== FormType.inboxSearch) {
            this.initFormElementPlaceholder();
            this.formField.isNameShown = this.isElementNameShown;
        }
    }

    initFormElementPlaceholder() {
        this.formField.placeholder = this.formField.placeholder || '';

        if (this.formField.type !== this.fieldTypesEnum.categories) {
            this.formField.placeholder +=
                this.formField.isRequired && !this.formField.placeholder.endsWith('*') ? ' *' : '';
        }
    }
}
